import type { Session } from '@/lib/types';
import { TrackBadge } from './TrackBadge';

type SessionCardProps = {
  session: Session;
  canBook: boolean;
  onBook: (sessionId: number) => void;
  onCancel: (sessionId: number) => void;
  onOpenDetails?: (session: Session) => void;
  busySessionId?: number | null;
};

export function SessionCard({ session, canBook, onBook, onCancel, onOpenDetails, busySessionId }: SessionCardProps) {
  const isBusy = busySessionId === session.id;
  const hasDetails = Boolean(session.description?.trim());
  const seatsLabel = session.isSoldOut
      ? 'Ausgebucht'
      : `${session.remainingSeats} freie Plätze`;

  return (
    <article className={`session-card ${session.isSelectedByMe ? 'session-card--selected' : ''} ${!session.isBookable ? 'session-card--static' : ''}`}>
      <div className="session-card__topline">
        <TrackBadge track={session.track} />
        <span className="session-card__format">{session.formatLabel}</span>
        {!session.isBookable ? <span className="status-pill status-pill--static">Nicht buchbar</span> : null}
      </div>

      <h3>{session.title}</h3>
      {session.subtitle ? <p className="session-card__subtitle">{session.subtitle}</p> : null}

      <dl className="session-card__meta">
        <div>
          <dt>Referentin</dt>
          <dd>{session.speaker ?? 'Wird noch ergänzt'}</dd>
        </div>
        <div>
          <dt>Raum</dt>
          <dd className="room-label">{session.room.name}</dd>
        </div>
        <div>
          <dt>Kapazität</dt>
          <dd>{session.isBookable ? `${session.capacity} Plätze · ${seatsLabel}` : 'Programmpunkt ohne Buchung'}</dd>
        </div>
      </dl>

      {session.speakerTag ? <p className="session-card__speaker-tag">{session.speakerTag}</p> : null}

      <div className="session-card__actions">
        {hasDetails ? (
          <button
            className="button button--ghost button--icon"
            type="button"
            onClick={() => onOpenDetails?.(session)}
            aria-label={`Mehr Infos zu ${session.title}`}
          >
            +
          </button>
        ) : (
          <span className="session-card__action-spacer" />
        )}

        {session.isSelectedByMe ? (
          <button className="button button--ghost session-card__action-button" type="button" onClick={() => onCancel(session.id)} disabled={isBusy}>
            {isBusy ? 'Abmeldung…' : 'Abmelden'}
          </button>
        ) : canBook && session.isBookable ? (
          <button
            className="button session-card__action-button"
            type="button"
            onClick={() => onBook(session.id)}
            disabled={isBusy || session.isSoldOut}
          >
            {isBusy ? 'Speichert…' : 'Anmelden'}
          </button>
        ) : (
          <span className="session-card__action-spacer" />
        )}
      </div>
    </article>
  );
}
